import React, { useRef, useState } from "react";
import { Pencil, Copy, Download, Trash2, Save } from "lucide-react";
import EmptyState from "../common/EmptyState";
import Button from "../common/Button";

function SessionRow({ session, onLoad, onRename, onDuplicate, onDelete, onExport }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(session.name);
  // Set once Enter/Escape/blur has finished this edit, so the blur that
  // follows unmounting the input can't commit (or re-commit) it.
  const finishedRef = useRef(false);

  const startRename = () => {
    // Always start from the current saved name, not a draft left over from
    // a previous (cancelled or emptied) edit.
    setName(session.name);
    finishedRef.current = false;
    setEditing(true);
  };

  const commitRename = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setEditing(false);
    const trimmed = name.trim();
    if (trimmed && trimmed !== session.name) onRename(session.id, trimmed);
  };

  const cancelRename = () => {
    finishedRef.current = true;
    setEditing(false);
    setName(session.name);
  };

  const handleRenameKeyDown = (event) => {
    if (event.key === "Enter") {
      commitRename();
    } else if (event.key === "Escape") {
      // Keep Escape from also closing the surrounding drawer.
      event.stopPropagation();
      cancelRename();
    }
  };

  return (
    <li className="session-row">
      {editing ? (
        <input
          className="session-rename-input"
          value={name}
          autoFocus
          onChange={(event) => setName(event.target.value)}
          onBlur={commitRename}
          aria-label={`New name for ${session.name}`}
          onKeyDown={handleRenameKeyDown}
        />
      ) : (
        <button type="button" className="session-row-main" onClick={() => onLoad(session)}>
          <span className="session-name">{session.name}</span>
          <span className="session-meta">
            {session.selectedDeviceIds.length} device(s) · {new Date(session.updatedAt).toLocaleDateString()}
          </span>
        </button>
      )}

      <div className="session-row-actions">
        <button type="button" className="icon-btn" aria-label={`Rename ${session.name}`} onClick={startRename}>
          <Pencil size={14} strokeWidth={1.75} aria-hidden="true" />
        </button>
        <button type="button" className="icon-btn" aria-label={`Duplicate ${session.name}`} onClick={() => onDuplicate(session.id)}>
          <Copy size={14} strokeWidth={1.75} aria-hidden="true" />
        </button>
        <button type="button" className="icon-btn" aria-label={`Export ${session.name}`} onClick={() => onExport(session.id)}>
          <Download size={14} strokeWidth={1.75} aria-hidden="true" />
        </button>
        <button type="button" className="icon-btn" aria-label={`Delete ${session.name}`} onClick={() => onDelete(session.id)}>
          <Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />
        </button>
      </div>
    </li>
  );
}

function SessionPanel({ sessions, canSave, onSave, onLoad, onRename, onDuplicate, onDelete, onExport, onImport, importError }) {
  const [newName, setNewName] = useState("");
  const fileInputRef = useRef(null);

  const handleSave = (event) => {
    event.preventDefault();
    if (!canSave) return;
    onSave(newName.trim() || "Untitled session");
    setNewName("");
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onImport(String(reader.result || ""));
    reader.readAsText(file);
    event.target.value = "";
  };

  return (
    <div className="session-panel">
      <form className="session-save-form" onSubmit={handleSave}>
        <label htmlFor="session-name-input" className="sr-only">
          Session name
        </label>
        <input
          id="session-name-input"
          type="text"
          placeholder="Name this session…"
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          disabled={!canSave}
        />
        <Button type="submit" variant="primary" disabled={!canSave}>
          Save current
        </Button>
      </form>

      <div className="panel-actions">
        <button type="button" className="link-btn" onClick={handleImportClick}>
          Import session
        </button>
        <input
          type="file"
          accept="application/json"
          ref={fileInputRef}
          className="sr-only"
          onChange={handleFileChange}
        />
      </div>

      {importError && (
        <p className="field-message field-message-error" role="alert">
          {importError}
        </p>
      )}

      {sessions.length === 0 ? (
        <EmptyState icon={Save} title="No saved sessions" description="Save your current URL, devices, and layout to reload it later." />
      ) : (
        <ul className="session-list">
          {sessions.map((session) => (
            <SessionRow
              key={session.id}
              session={session}
              onLoad={onLoad}
              onRename={onRename}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onExport={onExport}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

export default SessionPanel;
